import React, { useState } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Check, UserX, RotateCcw, KeyRound } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "Never");

const DecisionBadge = ({ decision }) => {
  const styles = { keep: "bg-emerald-100 text-emerald-700", remove: "bg-red-100 text-red-700", pending: "bg-amber-100 text-amber-700" };
  const labels = { keep: "Kept", remove: "Removed", pending: "Pending" };
  return <Badge className={`text-xs rounded-sm ${styles[decision]}`}>{labels[decision]}</Badge>;
};

function ReviewRow({ item, open, currentUserId, onDecide, onRestore, onResetMfa }) {
  const [note, setNote] = useState(item.note || "");
  const tid = item.email.replace(/[^a-z0-9]/gi, "-");
  return (
    <tr className="border-b last:border-0 align-top" data-testid={`review-row-${tid}`}>
      <td className="py-3 pr-3">
        <p className="text-sm font-medium text-slate-900">{item.name || "—"}</p>
        <p className="text-xs text-slate-500">{item.email}</p>
      </td>
      <td className="py-3 pr-3 text-xs text-slate-600 capitalize">{item.role.replace(/_/g, " ")}<br />{item.department}</td>
      <td className="py-3 pr-3 text-xs text-slate-600">{fmt(item.last_login_at)}</td>
      <td className="py-3 pr-3">
        <Badge variant="outline" className={`text-xs rounded-sm ${item.mfa_enabled ? "text-emerald-700" : "text-slate-500"}`}>{item.mfa_enabled ? "On" : "Off"}</Badge>
      </td>
      <td className="py-3 pr-3"><DecisionBadge decision={item.decision} />{item.decided_by && <p className="text-[11px] text-slate-400 mt-1">by {item.decided_by}</p>}</td>
      <td className="py-3 pr-3 min-w-[180px]">
        {open ? (
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (optional)" className="h-8 text-xs rounded-sm" data-testid={`review-note-${tid}`} />
        ) : <span className="text-xs text-slate-500">{item.note}</span>}
      </td>
      <td className="py-3">
        <div className="flex flex-wrap gap-1.5 justify-end">
          {open && (
            <>
              <Button size="sm" variant="outline" className="h-8 rounded-sm gap-1" onClick={() => onDecide(item, "keep", note)} data-testid={`review-keep-${tid}`}><Check size={13} /> Keep</Button>
              <Button size="sm" variant="outline" className="h-8 rounded-sm gap-1 text-red-700 border-red-200 hover:bg-red-50" disabled={item.user_id === currentUserId} onClick={() => onDecide(item, "remove", note)} data-testid={`review-remove-${tid}`}><UserX size={13} /> Remove</Button>
            </>
          )}
          {!item.is_active && (
            <Button size="sm" variant="ghost" className="h-8 rounded-sm gap-1" onClick={() => onRestore(item)} data-testid={`review-restore-${tid}`}><RotateCcw size={13} /> Restore</Button>
          )}
          {item.mfa_enabled && item.is_active && (
            <Button size="sm" variant="ghost" className="h-8 rounded-sm gap-1 text-slate-500" onClick={() => onResetMfa(item)} data-testid={`review-reset-mfa-${tid}`}><KeyRound size={13} /> Reset MFA</Button>
          )}
        </div>
      </td>
    </tr>
  );
}

export function ReviewTable({ review, currentUserId, onDecide, onRestore, onResetMfa }) {
  const open = review.status === "in_progress";
  return (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid="access-review-table">
        <thead>
          <tr className="border-b text-left text-[11px] font-mono uppercase tracking-wider text-slate-500">
            <th className="py-2 pr-3">User</th><th className="py-2 pr-3">Role</th><th className="py-2 pr-3">Last sign-in</th>
            <th className="py-2 pr-3">MFA</th><th className="py-2 pr-3">Decision</th><th className="py-2 pr-3">Note</th><th className="py-2" />
          </tr>
        </thead>
        <tbody>
          {review.items.map((item) => (
            <ReviewRow key={item.user_id} item={item} open={open} currentUserId={currentUserId} onDecide={onDecide} onRestore={onRestore} onResetMfa={onResetMfa} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
